import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '../../ui/button';
import { isMobileContainer } from '../../../utils/device';
import CrmChatEmployeeList from './CrmChatEmployeeList';
import CrmChatConversation from './CrmChatConversation';

export default function CrmChatPanel({
  open,
  onClose,
  employees,
  loadingEmployees,
  searchQuery,
  onSearchChange,
  activeContact,
  onSelectEmployee,
  onBack,
  messages,
  loadingMessages,
  loadingOlder,
  hasMoreMessages,
  onSend,
  onSendAttachment,
  onLoadOlder,
  replyTo,
  onCancelReply,
  onReply,
  onScrollToReply,
  uploadProgress,
  highlightedMessageId,
  registerMessageRef,
  listContainerRef,
  pendingNewCount = 0,
  onNearBottomChange,
  onClearPendingNew,
  consumeJustSent,
}) {
  const panelRef = useRef(null);
  const isMobile = isMobileContainer();
  const [keyboardInset, setKeyboardInset] = useState(0);
  const showConversation = isMobile ? !!activeContact : true;
  const showList = isMobile ? !activeContact : true;

  useEffect(() => {
    if (!open || !isMobile) {
      setKeyboardInset(0);
      return undefined;
    }
    const viewport = window.visualViewport;
    if (!viewport) return undefined;

    const updateInset = () => {
      const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      setKeyboardInset(inset);
    };

    updateInset();
    viewport.addEventListener('resize', updateInset);
    viewport.addEventListener('scroll', updateInset);
    return () => {
      viewport.removeEventListener('resize', updateInset);
      viewport.removeEventListener('scroll', updateInset);
    };
  }, [open, isMobile]);

  useEffect(() => {
    if (!open || !isMobile) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, isMobile]);

  if (!open) return null;

  const panelStyle = isMobile && keyboardInset > 0
    ? {
        height: `calc(100dvh - ${keyboardInset}px)`,
        bottom: `${keyboardInset}px`,
      }
    : undefined;

  const panelClassName = isMobile
    ? 'fixed inset-0 z-[70] w-full h-[100dvh] max-w-none rounded-none border-0 shadow-none bg-background overflow-hidden flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-300'
    : 'fixed z-[60] right-4 md:right-6 bottom-[calc(var(--mobile-nav-offset,0px)+5.5rem)] md:bottom-24 w-[calc(100vw-2rem)] max-w-[920px] h-[min(600px,calc(100vh-8rem))] rounded-2xl border border-border bg-background shadow-2xl overflow-hidden flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-300';

  const panel = (
    <div
      ref={panelRef}
      style={panelStyle}
      className={panelClassName}
      role="dialog"
      aria-label="CRM Chat"
    >
      <div className={`flex items-center justify-between px-3 py-2 border-b border-border bg-card shrink-0 ${isMobile ? 'pt-safe' : ''}`}>
        <span className="text-sm font-semibold text-foreground">CRM Chat</span>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} aria-label="Close chat">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-1 min-h-0">
        {showList && (
          <div
            className={`${
              isMobile ? 'w-full' : 'w-[340px] shrink-0'
            } flex flex-col min-h-0 ${isMobile && activeContact ? 'hidden' : ''}`}
          >
            <CrmChatEmployeeList
              employees={employees}
              loading={loadingEmployees}
              searchQuery={searchQuery}
              onSearchChange={onSearchChange}
              activeContact={activeContact}
              onSelect={onSelectEmployee}
            />
          </div>
        )}

        {showConversation && (
          <div
            className={`flex-1 min-w-0 flex flex-col min-h-0 ${
              isMobile && !activeContact ? 'hidden' : ''
            }`}
          >
            {activeContact ? (
              <CrmChatConversation
                contact={activeContact}
                messages={messages}
                loadingMessages={loadingMessages}
                loadingOlder={loadingOlder}
                hasMoreMessages={hasMoreMessages}
                onBack={onBack}
                onSend={onSend}
                onSendAttachment={onSendAttachment}
                onLoadOlder={onLoadOlder}
                replyTo={replyTo}
                onCancelReply={onCancelReply}
                onReply={onReply}
                onScrollToReply={onScrollToReply}
                uploadProgress={uploadProgress}
                highlightedMessageId={highlightedMessageId}
                registerMessageRef={registerMessageRef}
                listContainerRef={listContainerRef}
                showBackButton={isMobile}
                pendingNewCount={pendingNewCount}
                onNearBottomChange={onNearBottomChange}
                onClearPendingNew={onClearPendingNew}
                consumeJustSent={consumeJustSent}
              />
            ) : (
              <div className="hidden md:flex flex-1 flex-col items-center justify-center text-center p-8 bg-muted/20">
                <div className="text-muted-foreground text-sm max-w-xs">
                  Select a CRM employee from the list to start or continue a conversation.
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return isMobile ? createPortal(panel, document.body) : panel;
}
